import type { Dict } from "@/types/dict";
import type { IconFactor, SVGIcon } from "@/types/elements/icon";
import { join } from "math";
import type {
  RouteLocationMatched,
  RouteLocationNormalizedLoadedGeneric,
  RouteLocationResolvedGeneric,
} from "vue-router";

export type Crumb = {
  path: string;
  name: string | ComputedRef<string>;
  active?: boolean;
  icon?: IconFactor;
  ghost?: boolean;
  params?: Dict;
  loading?: boolean | ComputedRef<boolean>;
  breadcrumbClickable?: boolean;
};

export type UseBreadcrumbs = {
  crumbs: ComputedRef<Crumb[]>;
};

const makeCrumbs = (path: string) => {
  const crumbs: Crumb[] = [];
  const parts = path.split("/").filter((it) => it !== "");

  if (parts.length <= 0) return [];

  let a = parts[0];
  const currentCrumbs: string[] = [a];
  crumbs.push({
    path: "/" + a,
    name: a,
  });
  for (let i = 1; i < parts.length; i++) {
    const b = parts[i];
    currentCrumbs.push(b);

    crumbs.push({
      path: "/" + join(currentCrumbs, { suffix: () => "/" }).join(""),
      name: b,
    });

    a = b;
  }

  return crumbs;
};

const getRouteName = (
  route:
    | RouteLocationNormalizedLoadedGeneric
    | RouteLocationResolvedGeneric
    | RouteLocationMatched,
) => {
  if (typeof route.meta.breadcrumb === "string")
    return route.meta.breadcrumb + "";
  return (
    route.name ||
    route.meta.name ||
    (typeof route.meta.title === "string" ? route.meta.title : undefined) ||
    route.path
  ).toString();
};

const activeCrumbTitle = ref<string | undefined | null>(null);
const activeCrumbLoading = ref<boolean>(false);

export const setCrumbName = (name: string | undefined | null) => {
  activeCrumbTitle.value = name;
}


export const useActiveCrumbName = (nameRef: Ref<string | undefined | null>) => {
  activeCrumbLoading.value = true;

  const stopWatch = watch(nameRef, (name) => {
    setCrumbName(name);

    if (name) {
      setTimeout(() => {
        activeCrumbLoading.value = false;
      }, 200);
    }

  }, { immediate: true });

  onBeforeRouteLeave(() => {
    activeCrumbLoading.value = true;
    stopWatch();
    setCrumbName(null);

    setTimeout(() => {
      activeCrumbLoading.value = false;
    }, 300);
  })
}

const crumbHasID = (crumb: Crumb): boolean => {
  const params = crumb.params;
  if (!params) return false;
  const lastSegment = crumb.path.split('/').filter(Boolean).pop();
  if (!lastSegment) return false;
  return Object.entries(params).some(
    ([key, value]) => key.toLowerCase().includes('id') && value === lastSegment
  );
}

export const useBreadcrumbs = (): UseBreadcrumbs => {
  const route = useRoute();
  const router = useRouter();

  const crumbs = computed((): Crumb[] => {
    return (() => {
      const leafIcon = route.meta?.icon as SVGIcon | undefined;

      const items = makeCrumbs(route.path).map((crumb, i, arr): Crumb => {
        const resolved = router.resolve(crumb.path);

		const breadcrumbClickable = Boolean(resolved.meta?.breadcrumbClickable);
        const resolvedIcon = resolved?.meta?.icon as SVGIcon | undefined;
        const effectiveIcon = resolvedIcon?.src ? resolvedIcon : leafIcon;

        return {
          ...crumb,
          active: i === arr.length - 1,
          name: getRouteName(resolved),
          icon: effectiveIcon ? {
            ...effectiveIcon,
            src: effectiveIcon.src
          } : undefined,
          ghost: resolved.matched.length <= 1 && !breadcrumbClickable,
          params: resolved.params || {}
        };
      });

      if (items.length <= 0) {
        return route.matched.map((item, i, arr): Crumb => {
          return {
            path: item.path,
            name: getRouteName(item),
            icon: item.meta.icon as unknown as any,
            active: i === arr.length - 1,
            ghost: arr.length === 1,
            params: {}
          };
        });
      }

      return items;
    })().map((it) => {
      return {
        ...it,
        loading: computed(() => {
          const isID = crumbHasID(it);
          return isID && activeCrumbLoading.value;
        }),
        name: computed(() => {
          const activeName = activeCrumbTitle.value;
          const isID = crumbHasID(it);
          const name = isID && typeof activeName === 'string' ? activeName: unref(it.name);

          if (name.includes('-')) {
            const parts = name.split('-');
            return parts[parts.length-1];
          }

          if (name.includes('/')) {
            const parts = name.split('/');
            return parts[parts.length-1];
          }

          return name;
        })
      }
    });
  });

  return { crumbs };
};
